import { service } from '@vgw/pok-poker-table-client-lib/@vgw/pok-poker-table-client-lib'
import { appCtx } from '../../ApplicationContext'
import { GameMode } from '@vgw/pok-gameplay-protocol'
import { CurrencyCode } from '../../constants'
import { AuthService } from '../../auth/AuthService'
import { PokerTableService } from '../PokerTableService'
import { RingGameApiService } from './RingGameApiService'
import { checkTimeOut } from '../../redux/player/ResponsibleGamingActions'
import { TimeoutType } from '../../communication/PlayerApiProtocol.generated'
import { RestrictionsManager } from '../../RestrictionsManager'
import LobbyPlayerGamesData, {
	RingGamePlayerGame,
	RingGameSessionClosedReason,
	RingGameSessionStatus,
} from '../../LobbyPlayerGamesData'
import { GameType } from '../../LobbyManager'
import { Injector } from '../../injector'
import { DialogManager } from '../../ui/dialog/DialogManager'
import { ClientConfig } from '../../config/ClientConfig'
import { getStore } from '../../redux/Store'
import { getActiveBalance } from '../../data/BalanceUtils'
import { mapNegativeBalanceToZero } from '../../Utils'
import { LogManager } from '../../LogManager'
import { createDebugLogger } from 'shared/debug'

const debug = createDebugLogger('table')
import { myPlayer } from '../../MyPlayer'

export const RingGameManager = new (class {
	private tableService = service
	private state: { [gameId: string]: RingGame } = {}
	private tableSessionServices: { [gameId: string]: service.TableSessionService } = {}
	private dialogManager: DialogManager = Injector.get(DialogManager)

	private seatedTables = new Map<string, boolean>()
	private seatUpdateCallbacks = new Set<() => void>()

	async openTable(
		gameId: string,
		name: string,
		capacity: number,
		currencyCode: CurrencyCode,
		minBuyIn: number,
		maxBuyIn: number,
	): Promise<void> {
		if (appCtx.tableManager.getTable(gameId) && this.state[gameId]?.tableClosed === false && this.state[gameId]?.sessionStatus !== 'Closed') {
			appCtx.viewManager.activateViewByTableId(gameId)
			return
		}

		const playerId = await AuthService.getCurrentUserId()
		appCtx.tableManager.addTableName(gameId, name)

		const tableSessionService = this.tableService.buildTableSessionService(
			Number(playerId),
			gameId,
			{
				maxReconnectionDurationMs: 5 * 60 * 1000, // 5 minutes
				maxReconnectionDelayMs: 5 * 1000, // 5 seconds
				minReconnectionDelayMs: 1000, // 1 second
				pingTimeoutMs: 5 * 1000, // 5 seconds
				pingIntervalMs: 7 * 1000, // 7 seconds
			},
			(callback: (url: String) => void) => {
				PokerTableService.getTableInfo(gameId).then((tableInfo) => {
				PokerTableService.authenticateWithTableServer(tableInfo.tableAuthUrl).then((token) => {
					callback(`${tableInfo.tableSocketUrl}/player/v1/tables/${gameId}/${playerId}/${token}/ws`)
				})
				}).catch((error) => {
					LogManager.error({ message: `Failed to connect to ring game table: tableId[${gameId}] playerId[${playerId}] error[${error?.message}]` })
					// If the error has a status, it means the request reached the server but something went wrong (e.g., table not found, authentication failed, etc.)
					if (error.status) {
						this.dialogManager.displayGenericDialog(
							{ translationKey: 'table-closed' },
							() => {
								appCtx.tableManager.leaveTable(gameId)
								return true
							},
						)
					}
				})
			},
		)
		this.tableSessionServices[gameId] = tableSessionService

		let sequence = 0
		appCtx.tableManager.handleOpenTableAccepted(
			gameId,
			capacity,
			false,
			() => {
				tableSessionService.init(() => {
					const isSeated = this.state[gameId]?.sessionStatus === 'Seated'
					if (isSeated)
						tableSessionService.join(globalMyPlayer.name, true)
					else
						tableSessionService.spectate(globalMyPlayer.name)
				}, (message: string) => {
					LogManager.error({ message })
				}, (eventId: string, eventType: string) => {
					debug('Ring Game Table event received. eventType[%s] eventId[%s] tableId[%s] sequence[%d]', eventType, eventId, gameId, ++sequence)
				}, (error: string)=> {
					appCtx.tableManager.handlePingFailure(gameId)
				})
			},
			currencyCode,
			undefined,
			GameMode.RING_GAME,
			undefined,
			undefined,
			tableSessionService,
			undefined,
		)

		if (this.state[gameId] === undefined) {
			this.state[gameId] = {
				id: gameId,
				playerId: Number(playerId),
				name: name,
				currencyCode: currencyCode,
				minBuyIn: minBuyIn,
				maxBuyIn: maxBuyIn,
				autoTopUpConfigEnabled: false,
				tableClosed: false,
				playerSittingOutNextHand: false,
				playerIsAway: false,
			}
		}
	}

	subscribeToPlayerGameLobby() {
		LobbyPlayerGamesData.subscribeToPlayerGames((games) => {
			this.updateSeatedTables(games)

			this.handleGameUpdate(
				games
				.filter((game => game.type === GameType.Ring))
				.map(game => game as RingGamePlayerGame),
			)
		})
	}

	private async handleGameUpdate(games: RingGamePlayerGame[]) {
		games.forEach((game) => {
			const previousGameState = this.state[game.id]
			const updatedGameState = applyPlayerGameUpdate(game, previousGameState)
			this.state[updatedGameState.id] = updatedGameState
			if (previousGameState === undefined && updatedGameState.sessionStatus === 'Seated') {
				this.openTable(
					updatedGameState.id,
					updatedGameState.name,
					0,
					updatedGameState.currencyCode,
					updatedGameState.minBuyIn,
					updatedGameState.maxBuyIn,
				)
			}
			if (updatedGameState.sessionStatus === 'Closed' && updatedGameState.sessionStatus !== previousGameState?.sessionStatus) {
				this.notifyPlayerRemovedFromGame(updatedGameState)
			}
		})
	}

	notifyPlayerRemovedFromGame(game: RingGame) {
		LogManager.metric({message: `notifyPlayerRemovedFromGame[${game.closedReason}] received for ring game gameId[${game.id}] playerId[${game.playerId}]`})
		const table = appCtx.tableManager.getNewTable(game.id)
		switch (game.closedReason) {
			case 'IpCollision':
				table.adaptor.displayTableMessage(
					i18n.t('dialogs.ring-game-player-remove-ip-collusion.header'),
					i18n.t('dialogs.ring-game-player-remove-ip-collusion.message'),
				)
				break
			case 'RugCheckFailed':
				table.adaptor.displayTableMessage(
					i18n.t('dialogs.ring-game-rug-check-failure.header'),
					i18n.t('dialogs.ring-game-rug-check-failure.message', { sprintf: [game.name] }),
				)
				break
			case 'BuyInFailed':
				if (game.tableClosed) {
					table.adaptor.closeTableMessage(
						i18n.t('dialogs.table-closed-buy-in-failed.header'),
						i18n.t('dialogs.table-closed-buy-in-failed.message'),
					)
				} else {
					table.adaptor.displayTableMessage(
						i18n.t('dialogs.ring-game-unexpected-buy-in-error.header'),
						i18n.t('dialogs.ring-game-unexpected-buy-in-error.message'),
					)
				}
				break
			case 'GameCancelled':
				table.adaptor.closeTableMessage(
					i18n.t('dialogs.table-closed.header'),
					i18n.t('dialogs.table-closed.message'),
				)
				break
			case 'UnexpectedError':
				table.adaptor.closeTableMessage(
					i18n.t('dialogs.ring-game-unexpected-error.header'),
					i18n.t('dialogs.ring-game-unexpected-error.message'),
				)
				break
			case 'Inactivity':
				if (game.tableClosed) {
					table.adaptor.closeTableMessage(
						i18n.t('dialogs.table-closed-inactive.header'),
						i18n.t('dialogs.table-closed-inactive.message'),
					)
				} else {
					table.adaptor.displayTableMessage(
						i18n.t('dialogs.ring-game-player-removed.header'),
						i18n.t('dialogs.ring-game-player-removed.message'),
					)
				}
				break
			case 'SeatNotAvailable':
				table.adaptor.displayTableMessage(
					i18n.t('dialogs.ring-game-seat-not-available.header'),
					i18n.t('dialogs.ring-game-seat-not-available.message'),
				)
				break
			default:
				break
		}
	}

	async seatPlayer(gameId: string, seatId: string){
		if (ClientConfig.POKER_SERVER_TOURNAMENTS.TIMEOUTS_ENABLED && checkTimeOut(TimeoutType.POKER)) return
		RestrictionsManager.canPlay(() => {
			this.requestSeat(gameId, seatId)
				.then((res: any) => {
					return
				})
				.catch((error) => {
					// if the seat is already taken, we will get a 409 conflict error
					if (error?.status === 409) {
						const table = appCtx.tableManager.getNewTable(gameId)
						table.adaptor.displayTableMessage(
							i18n.t('dialogs.ring-game-seat-not-available.header'),
							i18n.t('dialogs.ring-game-seat-not-available.message'),
						)
					} else {
						LogManager.error({ message: `An error occurred while trying to seat the player: gameId[${gameId}] seatId[${seatId}]. The error details:[${error}]` })
					}
				})
		})()
	}

	async requestSeat(gameId: string, seatId: string): Promise<string>{
		return await RingGameApiService.seatPlayer(gameId, seatId)
	}

	async removePlayer(gameId: string): Promise<string>{
		return await RingGameApiService.removePlayer(gameId)
	}

	async buyIn(gameId: string, amount: number) {
		const game = this.state[gameId]
		return await RingGameApiService.buyIn(gameId, amount, !game.playerSittingOutNextHand && !game.playerIsAway)
	}

	removePlayerAfterCancelledBuyIn(gameId: string): boolean {
		const game = this.state[gameId]
		if (game.removePlayerIfBuyInCancelled) {
			this.removePlayer(gameId).catch()
			return true
		}
		return false
	}

	private calcMinMaxBuyInForPlayer(game: RingGame, balance: number): {min: number, max: number} {
		const maxBuyInForPlayer = Math.max(game.maxBuyIn - balance, 0)
		const minBuyInForPlayer = balance >= game.minBuyIn
			? 0
			: Math.min(game.minBuyIn - balance, maxBuyInForPlayer)
		return { min: minBuyInForPlayer, max: maxBuyInForPlayer }
	}

	// Table gameplay server send buy-in info to player
	handleServerRequestedPlayerToBuyIn(tableId: string, minBuyIn: number, maxBuyIn: number, isInitialBuyIn: boolean) {
		const game = this.state[tableId]
		if (game.autoTopUpConfigEnabled && !isInitialBuyIn) {
			this.topUpAutomatically(game, minBuyIn, maxBuyIn) // Player out of chips but will be automatically topped up
		} else {
			this.showBuyInDialog(game, minBuyIn, maxBuyIn, isInitialBuyIn) // Initial buy-in or player out of chips
		}
	}

	// Player clicked the Top-Up button under their avatar on the felt
	handlePlayerRequestedTopUpInfo(tableId: string, balance: number) {
		const game = this.state[tableId]
		const buyInForPlayer = this.calcMinMaxBuyInForPlayer(game, balance)
		this.showBuyInDialog(game, buyInForPlayer.min, buyInForPlayer.max, false)
	}

	// Called by felt when auto top-up meets criteria
	handleAutomaticTopUp(tableId: string, balance: number) {
		const game = this.state[tableId]
		const buyInForPlayer = this.calcMinMaxBuyInForPlayer(game, balance)
		this.topUpAutomatically(game, buyInForPlayer.min, buyInForPlayer.max)
	}

	private showBuyInDialog(game: RingGame, minBuyIn: number, maxBuyIn: number, removePlayerIfBuyInCancelled: boolean): void {
		game.removePlayerIfBuyInCancelled = removePlayerIfBuyInCancelled
		const activeWalletBalance = this.getActiveWalletBalanceForBuyIn(game)
		if (activeWalletBalance === undefined) return
		appCtx.tableManager.handleRingGameBuyInInfo(
			game.id,
			activeWalletBalance,
			game.currencyCode,
			Math.min(maxBuyIn, activeWalletBalance),
			minBuyIn,
			game.maxBuyIn,
			game.minBuyIn,
			false,
		)
	}

	private topUpAutomatically(game: RingGame, minBuyIn: number, maxBuyIn: number) {
		game.removePlayerIfBuyInCancelled = false
		const activeWalletBalance = this.getActiveWalletBalanceForBuyIn(game)
		if (activeWalletBalance === undefined) return
		appCtx.tableManager.handleRingGameBuyInInfo(
			game.id,
			activeWalletBalance,
			game.currencyCode,
			Math.min(maxBuyIn, activeWalletBalance),
			minBuyIn,
			game.maxBuyIn,
			game.minBuyIn,
			true,
		)
	}

	private getActiveWalletBalanceForBuyIn = (game: RingGame): number | undefined => {
		const walletBalances = getStore().getState().player.account.balances
		if (walletBalances.length === 0) {
			const table = appCtx.tableManager.getNewTable(game.id)
			table.adaptor.displayTableMessage(
				i18n.t('dialogs.ring-game-unexpected-buy-in-error.header'),
				i18n.t('dialogs.ring-game-unexpected-buy-in-error.message'),
			)
			return
		}
		return mapNegativeBalanceToZero(Number.parseFloat(getActiveBalance(game.currencyCode, walletBalances, false)))
	}

	setAutoTopUpConfigEnabled(tableId: string, enabled: boolean) {
		const game = this.state[tableId]
		game.autoTopUpConfigEnabled = enabled
	}

	async onLogout() {
		const promises = []
		for (const gameId in this.state) {
			const game = this.state[gameId]
			if (game.sessionStatus === 'Seated') {
				promises.push(this.removePlayer(gameId))
			}
		}
		if (promises.length > 0) {
			await Promise.all(promises)
		}
	}

	tableClosed(gameId: string): void {
		this.notifyTableClosed(gameId)
	}

	private notifyTableClosed(gameId: string) {
		const table = appCtx.tableManager.getNewTable(gameId)
		if (!table || !this.state[gameId]) return
		const closedReason = this.state[gameId]?.closedReason
		LogManager.metric({message: `notifyTableClosed[${closedReason}] received for ring game gameId[${gameId}] cubieaId[${myPlayer.id}]`})
		if (closedReason === 'Inactivity') {
			table.adaptor.closeTableMessage(
				i18n.t('dialogs.table-closed-inactive.header'),
				i18n.t('dialogs.table-closed-inactive.message'),
			)
		} if (closedReason === 'BuyInFailed') {
			table.adaptor.closeTableMessage(
				i18n.t('dialogs.table-closed-buy-in-failed.header'),
				i18n.t('dialogs.table-closed-buy-in-failed.message'),
			)
		} else if (closedReason === 'UnexpectedError' || closedReason === 'GameCancelled') {
			// Appropriate message already displayed
		} else {
			table.adaptor.closeTableMessage(
				i18n.t('dialogs.table-closed.header'),
				i18n.t('dialogs.table-closed.message'),
			)
		}
		this.state[gameId].tableClosed = true
	}

	playerSittingOutNextHand(gameId: string, sitOutNextHand: boolean): void {
		const game = this.state[gameId]
		if (game) {
			game.playerSittingOutNextHand = sitOutNextHand
		}
	}

	markPlayerAway(gameId: string, away: boolean): void {
		const game = this.state[gameId]
		if (game) {
			game.playerIsAway = away
		}
	}

	private updateSeatedTables(games: any[]): void {
		const ringGames = games.filter(g => g.type === GameType.Ring) as RingGamePlayerGame[]
		this.seatedTables.clear()

		ringGames.forEach(game => {
			if (game.sessionStatus === 'Seated') {
				this.seatedTables.set(String(game.id), true)
			}
		})

		this.notifySeatUpdate()
	}

	isSeatedAtTable(tableId: string | number): boolean {
		return this.seatedTables.get(String(tableId)) ?? false
	}

	onSeatUpdate(callback: () => void): () => void {
		this.seatUpdateCallbacks.add(callback)
		return () => this.seatUpdateCallbacks.delete(callback)
	}

	private notifySeatUpdate(): void {
		this.seatUpdateCallbacks.forEach(cb => cb())
	}
})

function applyPlayerGameUpdate(update: RingGamePlayerGame, current?: RingGame): RingGame {
	return {
		id: update.id,
		playerId: update.playerId,
		sessionId: update.sessionId,
		name: update.name ? update.name : current?.name,
		currencyCode: update.currencyCode ? update.currencyCode : current?.currencyCode,
		maxBuyIn: update.maxBuyIn ? update.maxBuyIn : current?.maxBuyIn,
		minBuyIn: update.minBuyIn ? update.minBuyIn : current?.minBuyIn,
		sessionStatus: update.sessionStatus ? update.sessionStatus : current?.sessionStatus,
		closedReason: update.closedReason ? update.closedReason : current?.closedReason,
		removePlayerIfBuyInCancelled: current ? current.removePlayerIfBuyInCancelled : false,
		autoTopUpConfigEnabled: current?.autoTopUpConfigEnabled || false,
		tableClosed: current?.tableClosed || false,
		playerSittingOutNextHand: current?.playerSittingOutNextHand || false,
		playerIsAway: current?.playerIsAway || false,
	} as RingGame
}

export type RingGame = {
	id: string,
	playerId: number,
	name: string,
	sessionId?: string,
	currencyCode: CurrencyCode,
	maxBuyIn?: number,
	minBuyIn?: number,
	sessionStatus?: RingGameSessionStatus,
	closedReason?: RingGameSessionClosedReason,
	removePlayerIfBuyInCancelled?: boolean,
	autoTopUpConfigEnabled: boolean,
	tableClosed: boolean,
	playerSittingOutNextHand: boolean,
	playerIsAway: boolean,
}
